import React, { useEffect, useState, useCallback, useRef } from 'react';
import Component from '../../components/Component';
import { BASE_URL } from '../../components/CommonExport';
import { DataGrid } from '@mui/x-data-grid';
import { Typography, Paper, TextField, IconButton, Dialog, DialogActions, DialogContent, DialogContentText, DialogTitle, Button, Autocomplete, Grid, Box, CircularProgress, Alert, Checkbox, FormControlLabel } from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import { Link } from 'react-router-dom';
import Papa from 'papaparse';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import axios from 'axios';

// ─── Auth helper: use session token, not hardcoded JWT ───────────────────────
function getAuthHeaders() {
  const token = sessionStorage.getItem('sessionToken');
  if (!token) return {};
  return { Authorization: `Bearer ${token}` };
}

const getTodayDate = () => {
  const now = new Date();
  const offsetMs = now.getTimezoneOffset() * 60000;
  const localDate = new Date(now.getTime() - offsetMs);
  return localDate.toISOString().split('T')[0];
};

/** GET /tracking/users may return users as data.users.rows, data.users[], or top-level users[]. */
function pickUsersList(payload) {
  if (!payload || typeof payload !== 'object') return [];
  const nested = payload.data?.users;
  if (Array.isArray(nested)) return nested;
  if (nested && Array.isArray(nested.rows)) return nested.rows;
  if (Array.isArray(payload.users)) return payload.users;
  return [];
}

function Users() {
  const [tableData, setTableData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [tableDisplay, setTableDisplay] = useState(false);
  const [dropdownUserData, setDropdownUserData] = useState([]);
  const [dropdownTrackingUserData, setDropdownTrackingUserData] = useState([]);
  const [dropdownMatchData, setDropdownMatchData] = useState([]);
  const [competitionFilter, setCompetitionFilter] = useState(null);
  const [usernameFilter, setUsernameFilter] = useState(null);
  const [fromDateFilter, setFromDateFilter] = useState(() => getTodayDate());
  const [toDateFilter, setToDateFilter] = useState(() => getTodayDate());
  const [matchFilter, setMatchFilter] = useState(null);
  const [selectedUsers, setSelectedUsers] = useState([]);
  const [usersAdded, setUsersAdded] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [deleteUserId, setDeleteUserId] = useState(null);
  const [sortModel, setSortModel] = useState([]);
  const [competitionData, setCompetitionData] = useState([]);
  const [competitionFilterId, setCompetitionFilterId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [searchText, setSearchText] = useState('');
  const [isStarFilter, setIsStarFilter] = useState(false);
  const [userDropdownSearch, setUserDropdownSearch] = useState('');
  const [userDropdownLoading, setUserDropdownLoading] = useState(false);

  // Ref to track if match data fetch is pending (prevents race condition)
  const matchFetchRef = useRef(0);

  // ─── Fetch tracking users for filter dropdown ──────────────────────────────
  useEffect(() => {
    fetch(`${BASE_URL}/tracking/trackingusers`, {
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
    })
      .then(response => response.json())
      .then(data => setDropdownTrackingUserData(data.data?.users?.rows ?? []))
      .catch(error => console.error('Error fetching tracking users:', error));
  }, []);

  // ─── Fetch users for "Add Users" dropdown (server search via ?search=) ───
  useEffect(() => {
    const q = userDropdownSearch.trim();
    const delay = q === '' ? 0 : 300;
    const controller = new AbortController();
    const timer = setTimeout(() => {
      const url = q
        ? `${BASE_URL}/tracking/users?search=${encodeURIComponent(q)}`
        : `${BASE_URL}/tracking/users`;
      setUserDropdownLoading(true);
      fetch(url, {
        signal: controller.signal,
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      })
        .then((response) => response.json())
        .then((data) => {
          if (!controller.signal.aborted) {
            setDropdownUserData(pickUsersList(data));
          }
        })
        .catch((error) => {
          if (error.name !== 'AbortError') console.error('Error fetching users:', error);
        })
        .finally(() => {
          if (!controller.signal.aborted) setUserDropdownLoading(false);
        });
    }, delay);
    return () => {
      clearTimeout(timer);
      controller.abort();
    };
  }, [userDropdownSearch]);

  // ─── Fetch competitions when dates change ──────────────────────────────────
  useEffect(() => {
    axios.get(`${BASE_URL}/admin/competitions/get_competitions?start_date=${fromDateFilter}&end_date=${toDateFilter}`, {
      headers: getAuthHeaders(),
    }).then((result) => {
      setCompetitionData(result.data?.data?.competitions ?? []);
    }).catch((error) => {
      console.error('Error fetching competitions:', error);
    });
  }, [fromDateFilter, toDateFilter]);

  // ─── Fetch matches when dates/competition change ───────────────────────────
  // FIX #2 (Race condition): Clear matchFilter when competition changes
  useEffect(() => {
    const fetchId = ++matchFetchRef.current;
    const requestBody = {};
    if (fromDateFilter) requestBody.startDate = fromDateFilter;
    if (toDateFilter) requestBody.endDate = toDateFilter;
    if (competitionFilterId) requestBody.cid = competitionFilterId;

    fetch(`${BASE_URL}/tracking/matches-by-cid`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(requestBody)
    })
      .then(response => response.json())
      .then(data => {
        // Only update if this is still the latest fetch (prevents race condition)
        if (fetchId === matchFetchRef.current) {
          setDropdownMatchData(data.data?.matches ?? []);
        }
      })
      .catch(error => console.error('Error fetching matches:', error));
  }, [fromDateFilter, toDateFilter, competitionFilterId]);

  // FIX #2: Clear match filter when competition changes (match might not exist in new competition)
  const handleCompetitionChange = (newValue) => {
    setCompetitionFilterId(newValue?.cid ?? null);
    setCompetitionFilter(newValue);
    setMatchFilter(null); // Clear match when competition changes
  };

  // ─── Fetch main user data when filters change ─────────────────────────────
  // FIX #3: Reset search when API filters change
  // FIX #4: Always set loading=false (even on error)
  // FIX #5: Robust match filter check
  const fetchUserData = useCallback(() => {
    const requestBody = {};
    if (fromDateFilter) requestBody.startDate = fromDateFilter;
    if (toDateFilter) requestBody.endDate = toDateFilter;
    if (isStarFilter) requestBody.isstar = true;
    if (usernameFilter && usernameFilter.user_id) requestBody.user_id = usernameFilter.user_id;
    if (matchFilter && matchFilter.match_id && matchFilter.id !== 'all') {
      requestBody.match_id = matchFilter.match_id;
    }
    if (competitionFilterId) requestBody.cid = competitionFilterId;

    setLoading(true);
    setError('');
    // FIX #3: Clear search text when re-fetching with new filters
    setSearchText('');

    fetch(`${BASE_URL}/tracking/fetchTrackingUserData`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(requestBody)
    })
      .then(response => {
        if (!response.ok) throw new Error(`HTTP ${response.status}`);
        return response.json();
      })
      .then(data => {
        const users = data.data?.users ?? [];
        setTableData(users);
        setFilteredData(users);
      })
      .catch(error => {
        console.error('Error fetching table data:', error);
        setError('Failed to load user data. Please try again.');
        setTableData([]);
        setFilteredData([]);
      })
      .finally(() => {
        // FIX #4: Always stop loading
        setLoading(false);
      });
  }, [fromDateFilter, toDateFilter, usernameFilter, matchFilter, competitionFilterId, isStarFilter]);

  useEffect(() => {
    fetchUserData();
  }, [fetchUserData]);

  // ─── Add users ─────────────────────────────────────────────────────────────
  const handleAddUsers = () => {
    const selectedUserData = selectedUsers.map(user => ({ id: user.id, username: user.username, name: user.name }));
    const requestBody = { users: selectedUserData };
    if (fromDateFilter) requestBody.startDate = fromDateFilter;
    if (toDateFilter) requestBody.endDate = toDateFilter;

    fetch(`${BASE_URL}/tracking/addUsersToView`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify(requestBody)
    })
      .then(response => {
        if (response.ok) setUsersAdded(true);
      })
      .catch(error => console.error('Error adding users:', error));
  };

  useEffect(() => {
    if (usersAdded) window.location.reload();
  }, [usersAdded]);

  // ─── Delete user ───────────────────────────────────────────────────────────
  const handleDeleteConfirmation = (userId) => {
    setDeleteUserId(userId);
    setConfirmDelete(true);
  };

  const handleDeleteUser = (userIdParam) => {
    const userIdToDelete = userIdParam ?? deleteUserId;
    if (!userIdToDelete) return;

    fetch(`${BASE_URL}/tracking/delete`, {
      method: 'DELETE',
      headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
      body: JSON.stringify({ user_id: userIdToDelete })
    })
      .then(response => {
        if (response.ok) {
          setTableData(prev => prev.filter(user => user.id !== userIdToDelete));
          setFilteredData(prev => prev.filter(user => user.id !== userIdToDelete));
          setConfirmDelete(false);
          setDeleteUserId(null);
        }
      })
      .catch(error => console.error('Error deleting user:', error));
  };

  // ─── Client-side search ────────────────────────────────────────────────────
  const handleSearch = (event) => {
    const value = event.target.value.toLowerCase();
    setSearchText(value);
    if (!value) {
      setFilteredData(tableData);
      return;
    }
    const filteredRows = tableData.filter((row) =>
      Object.values(row).some((field) =>
        String(field).toLowerCase().includes(value)
      )
    );
    setFilteredData(filteredRows);
  };

  // ─── Table display ─────────────────────────────────────────────────────────
  useEffect(() => {
    if (tableData && tableData.length > 0) setTableDisplay(true);
  }, [tableData]);

  // ─── CSV export ────────────────────────────────────────────────────────────
  const exportToCSV = () => {
    const csvData = filteredData.map(item => ({
      Username: item.username,
      Name: item.name,
      Views: item.views,
      TotalViews: item.totalviews,
      Posts: item.posts,
      Ratings: item.ratings,
      Likes: item.likes,
      UniqueLikes: item.uniquelikes,
      Comments: item.comments,
      UniqueComments: item.uniquecomments,
      CommentsByUser: item.commentsbyuser,
      TotalShare: item.totalshare,
      UniqueFeedShare: item.uniquefeedshare,
      AverageEngagementPerPost: item.averageengagementperpost,
      Engagement: item.engagement,
      PostScore: item.postscore,
    }));
    const csv = Papa.unparse(csvData);
    const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
    const link = document.createElement('a');
    const url = URL.createObjectURL(blob);
    link.setAttribute('href', url);
    link.setAttribute('download', 'data_export.csv');
    link.style.visibility = 'hidden';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  // ─── Format date ──────────────────────────────────────────────────────────
  const formatDate = (dateString) => {
    if (!dateString) return '';
    const date = new Date(dateString);
    const months = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];
    return `${months[date.getUTCMonth()]} ${date.getUTCDate()} ${date.getUTCFullYear()} ${(date.getUTCHours() % 12 || 12)}:${(date.getUTCMinutes() < 10 ? '0' : '') + date.getUTCMinutes()} ${date.getUTCHours() < 12 ? 'am' : 'pm'} IST`;
  };

  // ─── Columns ───────────────────────────────────────────────────────────────
  const columns = [
    { field: 'username', headerName: 'Username', width: 125 },
    {
      field: 'postscore',
      headerName: 'Post Score',
      width: 125,
      type: 'number',
      description: 'Average per-post engagement score across this user\'s posts in the filtered window (views + weighted likes/comments/shares). Computed live from real engagement data — see PR #940.',
    },
    { field: 'views', headerName: 'Views', width: 125, type: 'number' },
    { field: 'totalviews', headerName: 'Total Views', width: 125, type: 'number' },
    { field: 'posts', headerName: 'Posts', width: 125, type: 'number' },
    { field: 'ratings', headerName: 'Ratings', width: 125, type: 'number' },
    { field: 'likes', headerName: 'Likes', width: 125, type: 'number' },
    { field: 'uniquelikes', headerName: 'Unique Likes', width: 125, type: 'number' },
    { field: 'comments', headerName: 'Comments', width: 125, type: 'number' },
    { field: 'uniquecomments', headerName: 'Unique Comments', width: 125, type: 'number' },
    { field: 'commentsbyuser', headerName: 'Comments by User', width: 125, type: 'number' },
    { field: 'uniquefeedshare', headerName: 'Unique Feed Share', width: 125, type: 'number' },
    { field: 'totalshare', headerName: 'Total Share', width: 125, type: 'number' },
    { field: 'averageengagementperpost', headerName: 'Avg Engagement/Post', width: 125, type: 'number' },
    { field: 'engagement', headerName: 'Engagement', width: 125, type: 'number' },
    {
      field: 'action',
      headerName: 'Action',
      flex: 1,
      renderCell: (params) => (
        <IconButton onClick={() => handleDeleteConfirmation(params.row.id)}>
          <DeleteIcon />
        </IconButton>
      ),
    },
  ];

  // ─── Totals ────────────────────────────────────────────────────────────────
  const calculateTotals = (data) => ({
    totalViews: data.reduce((acc, row) => acc + Number(row.totalviews || 0), 0),
    totalPosts: data.reduce((acc, row) => acc + Number(row.posts || 0), 0),
    totalRatings: data.reduce((acc, row) => acc + Number(row.ratings || 0), 0),
    totalLikes: data.reduce((acc, row) => acc + Number(row.likes || 0), 0),
    totalComments: data.reduce((acc, row) => acc + Number(row.comments || 0), 0),
    totalEngagement: data.reduce((acc, row) => acc + Number(row.engagement || 0), 0).toFixed(2),
  });
  const totals = calculateTotals(filteredData);

  return (
    <Component>
      <div className="page-body">
        <div className="container-fluid">
          <div className="page-header">
            <div className="row">
              <div className="col-sm-12">
                <div className="d-flex align-items-center">
                  <IconButton style={{ background: '#FFFFFF' }} component={Link} to="/content-dashboard">
                    <ArrowBackIcon />
                  </IconButton>
                  <h3 style={{ marginLeft: '20px' }}>User Dashboard</h3>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="container-fluid">
          <div className="row">
            <div className="col-sm-12">
              <div className="card">
                <div className="card-body">

                  {/* Add Users Section */}
                  <Grid style={{ marginBottom: '16px' }} container spacing={2} alignItems="center">
                    <Grid item xs={10}>
                      <Autocomplete
                        multiple
                        id="user-selector"
                        options={dropdownUserData}
                        loading={userDropdownLoading}
                        filterOptions={(options) => options}
                        onInputChange={(event, newInputValue, reason) => {
                          if (reason === 'input' || reason === 'clear' || reason === 'reset') {
                            setUserDropdownSearch(newInputValue);
                          }
                        }}
                        getOptionLabel={(option) => `${option.id} - ${option.username} - ${option.name || ''} - ${option.email || ''} - ${option.phone || ''}`}
                        isOptionEqualToValue={(option, value) => option?.id === value?.id}
                        onChange={(event, newValue) => setSelectedUsers(newValue)}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            label="Select Users"
                            variant="outlined"
                            helperText="Type to search by username, name, or id"
                          />
                        )}
                        fullWidth
                      />
                    </Grid>
                    <Grid item xs={2}>
                      <Button style={{ width: '100%', height: '100%' }} variant="contained" color="primary" onClick={handleAddUsers} fullWidth>
                        Add Selected Users
                      </Button>
                    </Grid>
                  </Grid>
                  {usersAdded && (
                    <Typography variant="subtitle1" gutterBottom>
                      Following users added to tracker dashboard.
                    </Typography>
                  )}

                  {/* Filters Section */}
                  <Grid container spacing={2} sx={{ marginBottom: '20px' }}>
                    <Grid item xs={12} sm={6} md={4}>
                      <Autocomplete
                        id="username-filter"
                        options={dropdownTrackingUserData}
                        getOptionLabel={(option) => option ? `${option.user_id} - ${option.username} - ${option.name}` : ''}
                        value={usernameFilter}
                        onChange={(event, newValue) => setUsernameFilter(newValue)}
                        renderInput={(params) => <TextField {...params} label="Filter by Username" variant="outlined" fullWidth />}
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="From Date"
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={fromDateFilter}
                        onChange={(e) => setFromDateFilter(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={4}>
                      <TextField
                        label="To Date"
                        type="date"
                        variant="outlined"
                        fullWidth
                        value={toDateFilter}
                        onChange={(e) => setToDateFilter(e.target.value)}
                        InputLabelProps={{ shrink: true }}
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Autocomplete
                        id="competition-filter"
                        options={competitionData}
                        getOptionLabel={(option) => option ? `${option?.cid || ''} - ${option.title || ''} - ${option.game_format || ''}` : ''}
                        value={competitionFilter}
                        onChange={(event, newValue) => handleCompetitionChange(newValue)}
                        renderInput={(params) => <TextField {...params} label="Select Competition" variant="outlined" fullWidth />}
                        sx={{ backgroundColor: 'white' }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <Autocomplete
                        id="match-filter"
                        options={[{ id: 'all', match_id: null, title: 'All', name: 'All' }, ...dropdownMatchData]}
                        getOptionLabel={(option) => {
                          if (!option) return '';
                          if (option.id === 'all') return 'All Matches';
                          return `${option.match_id || ''}, ${option.title || ''}, ${option.subtitle || ''}, ${formatDate(option.date_start_ist)}`;
                        }}
                        value={matchFilter}
                        onChange={(event, newValue) => setMatchFilter(newValue)}
                        renderInput={(params) => <TextField {...params} label="Select Match" variant="outlined" fullWidth />}
                        sx={{ backgroundColor: 'white' }}
                        isOptionEqualToValue={(option, value) => {
                          if (!option || !value) return false;
                          if (option.id === 'all' && value.id === 'all') return true;
                          return option.match_id === value.match_id;
                        }}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <TextField
                        fullWidth
                        label="Search (filters current results)"
                        variant="outlined"
                        value={searchText}
                        onChange={handleSearch}
                        helperText={searchText ? `Showing ${filteredData.length} of ${tableData.length} results` : ''}
                      />
                    </Grid>
                    <Grid item xs={12} sm={6} md={6}>
                      <FormControlLabel
                        control={
                          <Checkbox
                            checked={isStarFilter}
                            onChange={(event) => setIsStarFilter(event.target.checked)}
                          />
                        }
                        label="Star users only"
                      />
                    </Grid>
                    <Grid item xs={12}>
                      <Button
                        style={{ marginTop: '16px' }}
                        variant="contained"
                        color="primary"
                        onClick={exportToCSV}
                      >
                        Export to CSV
                      </Button>
                    </Grid>
                  </Grid>

                  {/* Error Alert */}
                  {error && (
                    <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError('')}>
                      {error}
                    </Alert>
                  )}

                  {/* Totals Summary */}
                  <Box sx={{ backgroundColor: '#f9f9f9', border: '1px solid #ddd', padding: '16px', borderRadius: '8px', marginBottom: '20px' }}>
                    <Grid container spacing={2}>
                      <Grid item xs={6} sm={4}><Typography>Total Views: {totals.totalViews}</Typography></Grid>
                      <Grid item xs={6} sm={4}><Typography>Total Posts: {totals.totalPosts}</Typography></Grid>
                      <Grid item xs={6} sm={4}><Typography>Total Ratings: {totals.totalRatings}</Typography></Grid>
                      <Grid item xs={6} sm={4}><Typography>Total Likes: {totals.totalLikes}</Typography></Grid>
                      <Grid item xs={6} sm={4}><Typography>Total Comments: {totals.totalComments}</Typography></Grid>
                      <Grid item xs={6} sm={4}><Typography>Total Engagement: {totals.totalEngagement}</Typography></Grid>
                    </Grid>
                  </Box>

                  {/* Data Grid */}
                  {loading ? (
                    <Box sx={{ display: "flex", justifyContent: "center", alignItems: "center", height: "200px" }}>
                      <CircularProgress />
                    </Box>
                  ) : (
                    <DataGrid
                      rows={filteredData}
                      columns={columns}
                      pageSize={5}
                      rowsPerPageOptions={[5]}
                      sortingOrder={['asc', 'desc']}
                      sortModel={sortModel}
                      onSortModelChange={(model) => setSortModel(model)}
                      getRowId={(row) => row.id}
                    />
                  )}

                  {/* Delete Confirmation Dialog */}
                  <Dialog
                    open={confirmDelete}
                    onClose={() => setConfirmDelete(false)}
                  >
                    <DialogTitle>Confirm Deletion</DialogTitle>
                    <DialogContent>
                      <DialogContentText>
                        Are you sure you want to delete this user?
                      </DialogContentText>
                    </DialogContent>
                    <DialogActions>
                      <Button onClick={() => setConfirmDelete(false)} color="primary">Cancel</Button>
                      <Button onClick={() => handleDeleteUser(deleteUserId)} color="primary" autoFocus disabled={!deleteUserId}>Confirm</Button>
                    </DialogActions>
                  </Dialog>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Component>
  );
}

export default Users;
